import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { fetchData, postData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import {
    Tabs, Tab, Box, IconButton, Chip, LinearProgress, Tooltip, Fade,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from '@mui/material';

const DoctorEntryCreateIds = () => {
    const { id } = useParams();
    const history = useHistory();
    const { isAuthenticated, companyId, branchId, userId, token, logout } = useAuth();
    const [loading, setLoading] = useState(false);
    const [mounted, setMounted] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [formProgress, setFormProgress] = useState(0);
    const [validationErrors, setValidationErrors] = useState({});
    const [banner, setBanner] = useState({ show: false, message: '', type: '' });
    const [sectionCompletion, setSectionCompletion] = useState({
        patientInfo: false,
        observations: false,
        prescriptions: false,
        treatments: false,
        Questionnaire: false
    });

    // Patient Selection
    const [appointmentpatients, setAppointmentPatients] = useState();
    const [patients, setPatients] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [medicines, setMedicines] = useState([]);
    const [treatmentsList, setTreatmentsList] = useState([]);
    const [treatmentSessions, setTreatmentSessions] = useState([]);
    const [QuestionsList, setQuestionsList] = useState([]);
    const [isPatientModalOpen, setIsPatientModalOpen] = useState(false);
    const [patientSearchQuery, setPatientSearchQuery] = useState('');

    // Main Form Data
    const [formData, setFormData] = useState({
        CompanyId: companyId,
        CreatedBy: userId,
        PatientId: '',
        patientName: '',
        patientMRD: '',
        patientPhone: '',
        DoctorId: '',
        DoctorEntryDate: getCurrentDate(),
        ChiefComplaint: '',
        Diagnosis: ''
    });

    // Observations
    const [observations, setObservations] = useState([]);
    const [currentObservation, setCurrentObservation] = useState({
        ObservationType: '',
        ObservationValue: '',
        Unit: '',
        Remarks: ''
    });

    // Prescriptions
    const [prescriptions, setPrescriptions] = useState([]);
    const [currentPrescription, setCurrentPrescription] = useState({
        ItemId: '',
        MedicineName: '',
        Dosage: '',
        Frequency: '',
        Duration: '',
        Instructions: '',
        Quantity: 1,
    });

    // Treatments
    const [treatments, setTreatments] = useState([]);
    const [currentTreatment, setCurrentTreatment] = useState({
        TreatmentId: '',
        treatmentName: '',
        SessionId: '',
        sessionName: '',
        Description: '',
        StartDate: getCurrentDate(),
        EndDate: getCurrentDate(),
        Rate: '',
        SittingOrSession: 1,
    });

    // Questionnaire
    const [Questionnaire, setQuestionnaire] = useState([]);
    const [currentQuestion, setCurrentQuestion] = useState({
        QuestioniertId: '',
        Question: '',
        Answer: '',
        AnswerType: 'text' // 'text' or 'select'
    });

    // Patient History
    const [patientHistory, setPatientHistory] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [expandedHistoryIndex, setExpandedHistoryIndex] = useState(null);

    // Treatment Session Appointments
    const [sessionAppointments, setSessionAppointments] = useState([]);
    const [currentSessionAppointment, setCurrentSessionAppointment] = useState({
        TreatmentId: '',
        TreatmentName: '',
        SessionId: '',
        SessionName: '',
        Rate: '',
        AppointmentDate: getCurrentDate(),
        AppointmentTime: '09:00',
        Notes: ''
    });
    const [sessionTreatmentSessions, setSessionTreatmentSessions] = useState([]);
    const [isConsultationNeeded, setIsConsultationNeeded] = useState(false);
    const [consultationAmount, setConsultationAmount] = useState('');

    // Calculate total amount for session appointments
    const calculateTotalAmount = () => {
        const sessionTotal = sessionAppointments.reduce((sum, apt) => sum + (parseFloat(apt.Rate) || 0), 0);
        const consultationTotal = isConsultationNeeded ? (parseFloat(consultationAmount) || 0) : 0;
        return sessionTotal + consultationTotal;
    };

    useEffect(() => {
        setMounted(true);
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        fetchPatients();
        fetchDoctors();
        fetchMedicines();
        fetchTreatmentsList();
        fetchQuestionsList();
        fetchAppointments();
    }, [isAuthenticated, companyId, token, logout]);

    // Calculate form progress and section completion
    useEffect(() => {
        const requiredFields = [
            formData.PatientId,
            formData.DoctorId,
            formData.ChiefComplaint,
            observations.length > 0,
            prescriptions.length > 0
        ];
        const filledFields = requiredFields.filter(field => field).length;
        const progress = (filledFields / requiredFields.length) * 100;
        setFormProgress(Math.round(progress));

        // Update section completion
        setSectionCompletion({
            patientInfo: !!(formData.PatientId && formData.DoctorId && formData.ChiefComplaint),
            observations: observations.length > 0,
            prescriptions: prescriptions.length > 0,
            treatments: treatments.length > 0,
            Questionnaire: Questionnaire.length > 0
        });
    }, [formData, observations, prescriptions, treatments, Questionnaire]);

    // Validate input fields
    const validateField = (field, value) => {
        const errors = { ...validationErrors };

        switch (field) {
            case 'ChiefComplaint':
                if (value && value.length < 10) {
                    errors[field] = 'Chief complaint should be at least 10 characters';
                } else {
                    delete errors[field];
                }
                break;
            case 'Diagnosis':
                if (value && value.length < 5) {
                    errors[field] = 'Diagnosis should be at least 5 characters';
                } else {
                    delete errors[field];
                }
                break;
            default:
                break;
        }

        setValidationErrors(errors);
    };

    function getCurrentDate() {
        const today = new Date();
        return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    }

    const showBanner = (message, type = 'info') => {
        setBanner({ show: true, message, type });
        setTimeout(() => {
            setBanner({ show: false, message: '', type: '' });
        }, 4000);
    };

    const [loadingPatients, setLoadingPatients] = useState(false);

    const fetchPatients = async () => {
        setLoadingPatients(true);
        try {
            const response = await fetchData('PatientApi', `/${companyId}`, token);
            if (response.responseCode === '1111') {
                setPatients(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching patients:', error);
        } finally {
            setLoadingPatients(false);
        }
    };

    const fetchDoctors = async () => {
        try {
            const response = await fetchData('DoctorApi', `/${companyId}`, token);
            if (response.responseCode === '1111') {
                setDoctors(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching doctors:', error);
        }
    };

    const fetchMedicines = async () => {
        try {
            const response = await fetchData('ItemApi', `/${companyId}`, token);
            if (response.responseCode === '1111') {
                setMedicines(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching medicines:', error);
        }
    };

    const fetchTreatmentsList = async () => {
        try {
            const response = await fetchData('TreatmentApi', `/${companyId}`, token);
            if (response.responseCode === '1111') {
                setTreatmentsList(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching treatments:', error);
        }
    };

    const fetchTreatmentSessions = async (TreatmentId) => {
        try {
            const response = await fetchData('TreatmentSessionApi', `/TreatmentSession/${companyId}/${TreatmentId}`, token);
            if (response.responseCode === '1111') {
                setTreatmentSessions(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching treatment sessions:', error);
            setTreatmentSessions([]);
        }
    };

    const fetchQuestionsList = async () => {
        try {
            const response = await fetchData('QuestionierApi', `/${companyId}`, token);
            if (response.responseCode === '1111') {
                setQuestionsList(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching Questions:', error);
        }
    };

    // Fetch Patient History
    const fetchPatientHistory = async (patientId) => {
        if (!patientId) return;
        setIsLoadingHistory(true);
        try {
            const response = await fetchData('DoctorEntryApi', `/PatientHistorydetailsfromAppointment/${patientId}`, token);
            if (response.responseCode === '1111') {
                setPatientHistory(response.response_data || []);
            } else {
                setPatientHistory([]);
            }
        } catch (error) {
            console.error('Error fetching patient history:', error);
            setPatientHistory([]);
        } finally {
            setIsLoadingHistory(false);
        }
    };

    // Toggle expanded state for a visit row
    const toggleHistoryExpand = (index) => {
        setExpandedHistoryIndex(expandedHistoryIndex === index ? null : index);
    };

    // Fetch Treatment Sessions for Session Appointments
    const fetchSessionTreatmentSessions = async (TreatmentId) => {
        try {
            const response = await fetchData('TreatmentSessionApi', `/TreatmentSession/${companyId}/${TreatmentId}`, token);
            if (response.responseCode === '1111') {
                setSessionTreatmentSessions(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching treatment sessions:', error);
            setSessionTreatmentSessions([]);
        }
    };

    // Session Appointment Handlers
    const addSessionAppointment = () => {
        if (!currentSessionAppointment.TreatmentId || !currentSessionAppointment.SessionId) {
            showBanner('Please select treatment and session', 'warning');
            return;
        }
        if (!currentSessionAppointment.AppointmentDate || !currentSessionAppointment.AppointmentTime) {
            showBanner('Please select date and time', 'warning');
            return;
        }
        setSessionAppointments([...sessionAppointments, { ...currentSessionAppointment, id: Date.now() }]);
        setCurrentSessionAppointment({
            TreatmentId: '',
            TreatmentName: '',
            SessionId: '',
            SessionName: '',
            Rate: '',
            AppointmentDate: getCurrentDate(),
            AppointmentTime: '09:00',
            Notes: ''
        });
        setSessionTreatmentSessions([]);
        showBanner('Session appointment added successfully', 'success');
    };

    const deleteSessionAppointment = (id) => {
        setSessionAppointments(sessionAppointments.filter(apt => apt.id !== id));
        showBanner('Session appointment removed', 'info');
    };

    const handleFormChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        validateField(field, value);
    };

    // Observation Handlers
    const addObservation = () => {
        if (!currentObservation.ObservationType || !currentObservation.ObservationValue) {
            showBanner('Please fill observation type and value', 'warning');
            return;
        }
        setObservations([...observations, { ...currentObservation }]);
        setCurrentObservation({ ObservationType: '', ObservationValue: '', Unit: '', Remarks: '' });
        showBanner('Observation added successfully', 'success');
    };

    const deleteObservation = (id) => {
        setObservations(observations.filter(obs => obs.id !== id));
    };

    // Prescription Handlers
    const addPrescription = () => {
        if (!currentPrescription.MedicineName || !currentPrescription.Dosage) {
            showBanner('Please fill medicine name and dosage', 'warning');
            return;
        }
        setPrescriptions([...prescriptions, { ...currentPrescription }]);
        setCurrentPrescription({ ItemId: '', MedicineName: '', Dosage: '', Frequency: '', Duration: '', Instructions: '' });
        showBanner('Prescription added successfully', 'success');
    };

    const deletePrescription = (id) => {
        setPrescriptions(prescriptions.filter(pres => pres.id !== id));
    };

    // Treatment Handlers
    const addTreatment = () => {
        if (!currentTreatment.TreatmentId || !currentTreatment.treatmentName) {
            showBanner('Please select treatment', 'warning');
            return;
        }
        setTreatments([...treatments, { ...currentTreatment }]);
        setCurrentTreatment({
            TreatmentId: '',
            treatmentName: '',
            SessionId: '',
            sessionName: '',
            Description: '',
            StartDate: getCurrentDate(),
            EndDate: '',
            Rate: ''
        });
        setTreatmentSessions([]);
        showBanner('Treatment added successfully', 'success');
    };

    const deleteTreatment = (id) => {
        setTreatments(treatments.filter(treat => treat.id !== id));
    };

    // Questionnaire Handlers
    const addQuestion = () => {
        if (!currentQuestion.Question || !currentQuestion.Answer) {
            showBanner('Please fill question and answer', 'warning');
            return;
        }
        setQuestionnaire([...Questionnaire, { ...currentQuestion }]);
        setCurrentQuestion({ QuestioniertId: '', Question: '', Answer: '', AnswerType: 'text' });
        showBanner('Question added successfully', 'success');
    };

    const deleteQuestion = (id) => {
        setQuestionnaire(Questionnaire.filter(q => q.id !== id));
    };

    const handleSubmit = async () => {
        if (!formData.PatientId || !formData.DoctorId) {
            showBanner('Please select patient and doctor', 'error');
            return;
        }

        // Calculate total amount (session rates + consultation fee if checked)
        const sessionTotal = sessionAppointments.reduce((sum, apt) => sum + (parseFloat(apt.Rate) || 0), 0);
        const consultationFee = isConsultationNeeded ? (parseFloat(consultationAmount) || 0) : 0;
        const totalAmount = sessionTotal + consultationFee;

        // Prepare appointment list - includes session appointments with TreatmentSessionId
        const appointmentList = sessionAppointments.map(apt => ({
            TreatmentSessionId: parseInt(apt.SessionId) || null,
            AppointmentDate: apt.AppointmentDate,
            AppointmentTime: apt.AppointmentTime,
            //Amount: parseFloat(apt.Rate) +  consultationFee || 0,
            Amount : totalAmount || 0,
            Remark: `Advance booking on ${apt.AppointmentDate}`,
            IsConsultation: isConsultationNeeded
        }));

        const payload = {
            DoctorEntry: {
                ...formData,
                AppointmentId: id // Add the appointment ID from URL
            },
            ObservationDetails: observations,
            PrescriptionDetails: prescriptions,
            TreatmentDetails: treatments,
            TreatmentQuestionier: Questionnaire,
            Appointment: appointmentList,
           
        };

        try {
            setLoading(true);
            const response = await postData('DoctorEntryApi', payload, token);
            if (response.responseCode === '1111') {
                showBanner('Doctor Entry created successfully!', 'success');
                setTimeout(() => {
                    history.push('/DoctorEntryIndex');
                }, 1500);
            }
        } catch (error) {
            console.error('Error creating doctor entry:', error);
            showBanner('Failed to create doctor entry', 'error');
        } finally {
            setLoading(false);
        }
    };

    const labelStyle = {
        display: 'block',
        marginBottom: '2px',
        fontSize: '11px',
        fontWeight: '600',
        color: '#374151',
        textTransform: 'uppercase',
        letterSpacing: '0.3px'
    };

    const inputStyle = {
        width: '100%',
        padding: '6px 10px',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        fontSize: '13px',
        transition: 'all 0.2s ease',
        outline: 'none',
        height: '32px'
    };

    const buttonStyle = {
        padding: '6px 12px',
        borderRadius: '6px',
        border: 'none',
        fontSize: '12px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center',
        gap: '4px'
    };

    // Auto fetch the patient details from Appointments
    const [loadingAppointments, setLoadingAppointments] = useState(false);

    const fetchAppointments = async () => {
        setLoadingAppointments(true);
        try {
            const response = await fetchData('DoctorEntryApi', `/PatientdetailsfromAppointment/${id}`, token);
            if (response.responseCode === '1111') {
                setAppointmentPatients(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching appointment:', error);
        } finally {
            setLoadingAppointments(false);
        }
    };

    // Populate form fields when appointment data is loaded
    useEffect(() => {
        if (appointmentpatients) {
            // Check if it's an array or single object
            const appointmentData = Array.isArray(appointmentpatients) ? appointmentpatients[0] : appointmentpatients;

            if (appointmentData) {
                setFormData(prev => ({
                    ...prev,
                    PatientId: appointmentData.PatientId || '',
                    patientName: appointmentData.PatientName || '',
                    patientMRD: appointmentData.MRDNo || '',
                    patientPhone: appointmentData.ContactNumber1 || '',
                    DoctorId: appointmentData.DoctorId || '',
                    DoctorName: appointmentData.DoctorName || '',
                }));
                // Set consultation amount from appointment data
                setConsultationAmount(appointmentData.ConsultationAmount || '');
                // Fetch patient history when patient is loaded
                if (appointmentData.PatientId) {
                    fetchPatientHistory(appointmentData.PatientId);
                }
            }
        }
    }, [appointmentpatients]);

    return (
        <Fade in={mounted} timeout={800}>
            <div style={{ padding: '10px', maxWidth: '100%', position: 'relative' }}>
                {/* Loading Overlay */}
                {loading && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.5)',
                        backdropFilter: 'blur(4px)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10001,
                        animation: 'fadeIn 0.3s ease-out'
                    }}>
                        <div style={{
                            background: 'white',
                            padding: '40px 60px',
                            borderRadius: '16px',
                            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '20px',
                            animation: 'scaleIn 0.3s ease-out'
                        }}>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                border: '4px solid #e5e7eb',
                                borderTop: '4px solid #667eea',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                            }}></div>
                            <div style={{
                                fontSize: '16px',
                                fontWeight: '600',
                                color: '#374151',
                                textAlign: 'center'
                            }}>
                                Saving Doctor Entry...
                            </div>
                            <div style={{
                                fontSize: '13px',
                                color: '#6b7280',
                                textAlign: 'center'
                            }}>
                                Please wait while we save your data
                            </div>
                        </div>
                    </div>
                )}

                {/* Banner Notification */}
                {banner.show && (
                    <div style={{
                        position: 'fixed',
                        top: '20px',
                        right: '20px',
                        zIndex: 10000,
                        minWidth: '300px',
                        maxWidth: '500px',
                        padding: '16px 20px',
                        borderRadius: '12px',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        animation: 'slideInRight 0.4s ease-out',
                        background: banner.type === 'success'
                            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                            : banner.type === 'error'
                                ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                                : banner.type === 'warning'
                                    ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                                    : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                        color: 'white'
                    }}>
                        <span style={{ fontSize: '24px' }}>
                            {banner.type === 'success' ? '✅' : banner.type === 'error' ? '❌' : banner.type === 'warning' ? '⚠️' : 'ℹ️'}
                        </span>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '2px' }}>
                                {banner.type === 'success' ? 'Success' : banner.type === 'error' ? 'Error' : banner.type === 'warning' ? 'Warning' : 'Info'}
                            </div>
                            <div style={{ fontSize: '13px', opacity: 0.95 }}>{banner.message}</div>
                        </div>
                        <button
                            onClick={() => setBanner({ show: false, message: '', type: '' })}
                            style={{
                                background: 'rgba(255, 255, 255, 0.2)',
                                border: 'none',
                                borderRadius: '6px',
                                color: 'white',
                                cursor: 'pointer',
                                padding: '4px 8px',
                                fontSize: '18px',
                                lineHeight: '1',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
                            onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
                        >
                            ×
                        </button>
                    </div>
                )}
                <style>{`
          @keyframes slideInRight {
            from {
              opacity: 0;
              transform: translateX(100px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          @keyframes scaleIn {
            from {
              opacity: 0;
              transform: scale(0.8);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
          @keyframes slideIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes shimmer {
            0% { background-position: -1000px 0; }
            100% { background-position: 1000px 0; }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          @keyframes checkmark {
            0% { transform: scale(0) rotate(45deg); }
            50% { transform: scale(1.2) rotate(45deg); }
            100% { transform: scale(1) rotate(45deg); }
          }
          @keyframes fadeInUp {
            from { 
              opacity: 0; 
              transform: translateY(10px); 
            }
            to { 
              opacity: 1; 
              transform: translateY(0); 
            }
          }
          @keyframes tableRowAppear {
            from {
              opacity: 0;
              transform: translateX(-20px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          .advanced-table {
            animation: fadeInUp 0.5s ease-out;
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          }
          .table-row-animated {
            animation: tableRowAppear 0.4s ease-out;
            transition: all 0.3s ease;
          }
          .table-row-animated:hover {
            background: linear-gradient(90deg, #f8fafc 0%, #f1f5f9 100%) !important;
            transform: translateX(4px);
            box-shadow: -4px 0 0 0 #8b9aed;
          }
          .input-field {
            transition: all 0.2s ease;
          }
          .input-field:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
          }
          .input-field.error {
            border-color: #ef4444;
            background-color: #fef2f2;
          }
          .input-field.error:focus {
            box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
          }
          .input-field.success {
            border-color: #10b981;
            background-color: #f0fdf4;
          }
          select.input-field {
            border-color: #4ade80;
            background-color: #f0fdf4;
          }
          select.input-field:focus {
            border-color: #22c55e;
            box-shadow: 0 0 0 3px rgba(74, 222, 128, 0.1);
          }
          .tab-content {
            animation: slideIn 0.4s ease-out;
          }
          .shimmer-loading {
            background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
            background-size: 1000px 100%;
            animation: shimmer 2s infinite;
          }
          .completion-badge {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            padding: 5px 10px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 600;
            transition: all 0.3s ease;
          }
          .completion-badge.completed {
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            color: white;
          }
          .completion-badge.incomplete {
            background: #f3f4f6;
            color: #9ca3af;
          }
          .checkmark {
            display: inline-block;
            width: 12px;
            height: 12px;
            border-radius: 50%;
            background: white;
            position: relative;
          }
          .checkmark::after {
            content: '';
            position: absolute;
            left: 4px;
            top: 2px;
            width: 3px;
            height: 6px;
            border: solid #10b981;
            border-width: 0 2px 2px 0;
            transform: rotate(45deg);
            animation: checkmark 0.3s ease;
          }
        `}</style>

                <div style={{
                    maxWidth: '1400px',
                    margin: '0 auto',
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
                    overflow: 'hidden',
                    marginTop: '-30px'
                }}>
                    {/* Progress Bar */}
                    <LinearProgress
                        variant="determinate"
                        value={formProgress}
                        style={{ height: '3px' }}
                        sx={{
                            backgroundColor: '#e0f2e9',
                            '& .MuiLinearProgress-bar': {
                                background: formProgress === 100
                                    ? 'linear-gradient(90deg, #059669 0%, #10b981 100%)'
                                    : 'linear-gradient(90deg, #10b981 0%, #34d399 100%)'
                            }
                        }}
                    />

                    {/* Header */}
                    <div style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        padding: '8px 16px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '24px' }}>🏥</span>
                            <div>
                                <h1 style={{ fontSize: '15px', fontWeight: '700', color: 'white', margin: 0, lineHeight: '1.2' }}>
                                    Doctor Entry - Patient Record
                                </h1>
                                <p style={{ fontSize: '10px', color: 'rgba(255,255,255,0.9)', margin: '2px 0 0 0', lineHeight: '1.3' }}>
                                    Comprehensive patient examination and treatment documentation
                                </p>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <Tooltip title="Patient & Doctor Information" arrow placement="bottom">
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '3px 8px',
                                    borderRadius: '12px',
                                    height: '24px',
                                    background: sectionCompletion.patientInfo
                                        ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.95) 0%, rgba(5, 150, 105, 0.95) 100%)'
                                        : 'rgba(255, 255, 255, 0.15)',
                                    backdropFilter: 'blur(12px)',
                                    border: sectionCompletion.patientInfo
                                        ? '1px solid rgba(255, 255, 255, 0.3)'
                                        : '1px solid rgba(255, 255, 255, 0.2)',
                                    boxShadow: sectionCompletion.patientInfo
                                        ? '0 4px 12px rgba(16, 185, 129, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                                        : '0 2px 8px rgba(0, 0, 0, 0.1)',
                                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                    cursor: 'default'
                                }}>
                                    <span style={{ fontSize: '12px', lineHeight: '1' }}>👤</span>
                                    <span style={{
                                        fontSize: '9px',
                                        fontWeight: '700',
                                        color: 'white',
                                        letterSpacing: '0.3px'
                                    }}>
                                        Patient
                                    </span>
                                    {sectionCompletion.patientInfo && (
                                        <span style={{
                                            fontSize: '10px',
                                            color: 'white',
                                            animation: 'checkmark 0.5s ease'
                                        }}>✓</span>
                                    )}
                                </div>
                            </Tooltip>

                            <Tooltip title="Click to view Observations" arrow placement="bottom">
                                <div
                                    onClick={() => setActiveTab(0)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '3px 8px',
                                        borderRadius: '12px',
                                        height: '24px',
                                        background: sectionCompletion.observations
                                            ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.95) 0%, rgba(5, 150, 105, 0.95) 100%)'
                                            : 'rgba(255, 255, 255, 0.15)',
                                        backdropFilter: 'blur(12px)',
                                        border: sectionCompletion.observations
                                            ? '1px solid rgba(255, 255, 255, 0.3)'
                                            : '1px solid rgba(255, 255, 255, 0.2)',
                                        boxShadow: sectionCompletion.observations
                                            ? '0 4px 12px rgba(16, 185, 129, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                                            : '0 2px 8px rgba(0, 0, 0, 0.1)',
                                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                        cursor: 'pointer'
                                    }}>
                                    <span style={{ fontSize: '12px', lineHeight: '1' }}>🩺</span>
                                    <span style={{
                                        fontSize: '9px',
                                        fontWeight: '700',
                                        color: 'white',
                                        letterSpacing: '0.3px'
                                    }}>
                                        Observations
                                    </span>
                                    {sectionCompletion.observations && (
                                        <span style={{
                                            fontSize: '10px',
                                            color: 'white',
                                            animation: 'checkmark 0.5s ease'
                                        }}>✓</span>
                                    )}
                                </div>
                            </Tooltip>

                            <Tooltip title="Click to view Prescriptions" arrow placement="bottom">
                                <div
                                    onClick={() => setActiveTab(1)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '3px 8px',
                                        borderRadius: '12px',
                                        height: '24px',
                                        background: sectionCompletion.prescriptions
                                            ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.95) 0%, rgba(5, 150, 105, 0.95) 100%)'
                                            : 'rgba(255, 255, 255, 0.15)',
                                        backdropFilter: 'blur(12px)',
                                        border: sectionCompletion.prescriptions
                                            ? '1px solid rgba(255, 255, 255, 0.3)'
                                            : '1px solid rgba(255, 255, 255, 0.2)',
                                        boxShadow: sectionCompletion.prescriptions
                                            ? '0 4px 12px rgba(16, 185, 129, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                                            : '0 2px 8px rgba(0, 0, 0, 0.1)',
                                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                        cursor: 'pointer'
                                    }}>
                                    <span style={{ fontSize: '12px', lineHeight: '1' }}>💊</span>
                                    <span style={{
                                        fontSize: '9px',
                                        fontWeight: '700',
                                        color: 'white',
                                        letterSpacing: '0.3px'
                                    }}>
                                        Prescriptions
                                    </span>
                                    {sectionCompletion.prescriptions && (
                                        <span style={{
                                            fontSize: '10px',
                                            color: 'white',
                                            animation: 'checkmark 0.5s ease'
                                        }}>✓</span>
                                    )}
                                </div>
                            </Tooltip>

                            <Tooltip title="Click to view Treatments" arrow placement="bottom">
                                <div
                                    onClick={() => setActiveTab(2)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '3px 8px',
                                        borderRadius: '12px',
                                        height: '24px',
                                        background: sectionCompletion.treatments
                                            ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.95) 0%, rgba(5, 150, 105, 0.95) 100%)'
                                            : 'rgba(255, 255, 255, 0.15)',
                                        backdropFilter: 'blur(12px)',
                                        border: sectionCompletion.treatments
                                            ? '1px solid rgba(255, 255, 255, 0.3)'
                                            : '1px solid rgba(255, 255, 255, 0.2)',
                                        boxShadow: sectionCompletion.treatments
                                            ? '0 4px 12px rgba(16, 185, 129, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                                            : '0 2px 8px rgba(0, 0, 0, 0.1)',
                                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                        cursor: 'pointer'
                                    }}>
                                    <span style={{ fontSize: '12px', lineHeight: '1' }}>🏥</span>
                                    <span style={{
                                        fontSize: '9px',
                                        fontWeight: '700',
                                        color: 'white',
                                        letterSpacing: '0.3px'
                                    }}>
                                        Treatments
                                    </span>
                                    {sectionCompletion.treatments && (
                                        <span style={{
                                            fontSize: '10px',
                                            color: 'white',
                                            animation: 'checkmark 0.5s ease'
                                        }}>✓</span>
                                    )}
                                </div>
                            </Tooltip>

                            <Tooltip title="Click to view Questionnaire" arrow placement="bottom">
                                <div
                                    onClick={() => setActiveTab(3)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '3px 8px',
                                        borderRadius: '12px',
                                        height: '24px',
                                        background: sectionCompletion.Questionnaire
                                            ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.95) 0%, rgba(5, 150, 105, 0.95) 100%)'
                                            : 'rgba(255, 255, 255, 0.15)',
                                        backdropFilter: 'blur(12px)',
                                        border: sectionCompletion.Questionnaire
                                            ? '1px solid rgba(255, 255, 255, 0.3)'
                                            : '1px solid rgba(255, 255, 255, 0.2)',
                                        boxShadow: sectionCompletion.Questionnaire
                                            ? '0 4px 12px rgba(16, 185, 129, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                                            : '0 2px 8px rgba(0, 0, 0, 0.1)',
                                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                        cursor: 'pointer'
                                    }}>
                                    <span style={{ fontSize: '12px', lineHeight: '1' }}>❓</span>
                                    <span style={{
                                        fontSize: '9px',
                                        fontWeight: '700',
                                        color: 'white',
                                        letterSpacing: '0.3px'
                                    }}>
                                        Questions
                                    </span>
                                    {sectionCompletion.Questionnaire && (
                                        <span style={{
                                            fontSize: '10px',
                                            color: 'white',
                                            animation: 'checkmark 0.5s ease'
                                        }}>✓</span>
                                    )}
                                </div>
                            </Tooltip>

                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '3px 10px',
                                borderRadius: '12px',
                                height: '24px',
                                background: formProgress === 100
                                    ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.95) 0%, rgba(5, 150, 105, 0.95) 100%)'
                                    : 'rgba(255, 255, 255, 0.2)',
                                backdropFilter: 'blur(12px)',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                boxShadow: formProgress === 100
                                    ? '0 4px 12px rgba(16, 185, 129, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                                    : '0 2px 8px rgba(0, 0, 0, 0.1)',
                                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}>
                                <span style={{ fontSize: '14px', lineHeight: '1' }}>📊</span>
                                <span style={{
                                    fontSize: '11px',
                                    fontWeight: '700',
                                    color: 'white',
                                    letterSpacing: '0.5px'
                                }}>
                                    {formProgress}%
                                </span>
                            </div>

                            {/* Action Buttons */}
                            <div style={{ display: 'flex', gap: '6px', marginLeft: '8px', borderLeft: '1px solid rgba(255,255,255,0.3)', paddingLeft: '8px' }}>
                                <Tooltip title="Page Information & Help" arrow>
                                    <button
                                        onClick={() => history.push(`/DoctorEntryInfo/${id}`)}
                                        style={{
                                            ...buttonStyle,
                                            padding: '4px 10px',
                                            height: '26px',
                                            fontSize: '10px',
                                            background: 'rgba(255,255,255,0.2)',
                                            color: 'white',
                                            border: '1px solid rgba(255,255,255,0.3)',
                                            backdropFilter: 'blur(10px)'
                                        }}
                                    >
                                        <span style={{ fontSize: '14px' }}>ℹ️</span>
                                        Info
                                    </button>
                                </Tooltip>
                                <Tooltip title="Cancel and go back" arrow>
                                    <button
                                        onClick={() => history.goBack()}
                                        style={{
                                            ...buttonStyle,
                                            padding: '4px 10px',
                                            height: '26px',
                                            fontSize: '10px',
                                            background: 'rgba(255,255,255,0.2)',
                                            color: 'white',
                                            border: '1px solid rgba(255,255,255,0.3)',
                                            backdropFilter: 'blur(10px)'
                                        }}
                                    >
                                        <span style={{ fontSize: '12px' }}>❌</span>
                                        Cancel
                                    </button>
                                </Tooltip>
                                <Tooltip
                                    title={formProgress < 60 ? 'Complete at least 60% of the form to save' : 'Save doctor entry'}
                                    arrow
                                >
                                    <span>
                                        <button
                                            onClick={handleSubmit}
                                            disabled={loading || formProgress < 60}
                                            style={{
                                                ...buttonStyle,
                                                padding: '4px 10px',
                                                height: '26px',
                                                fontSize: '10px',
                                                background: loading || formProgress < 60
                                                    ? 'rgba(255,255,255,0.3)'
                                                    : 'rgba(255,255,255,0.95)',
                                                color: loading || formProgress < 60 ? 'rgba(255,255,255,0.7)' : '#667eea',
                                                border: '1px solid rgba(255,255,255,0.4)',
                                                opacity: loading || formProgress < 60 ? 0.6 : 1,
                                                cursor: loading || formProgress < 60 ? 'not-allowed' : 'pointer',
                                                fontWeight: '700'
                                            }}
                                        >
                                            <span style={{ fontSize: '12px' }}>💾</span>
                                            {loading ? 'Saving...' : 'Save Doctor Entry'}
                                        </button>
                                    </span>
                                </Tooltip>
                            </div>
                        </div>
                    </div>

                    {/* Patient & Doctor Selection */}
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', background: '#fafbfc' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
                            <div>
                                <label style={labelStyle}>
                                    Patient <span style={{ color: '#dc2626' }}>*</span>
                                </label>
                                <Tooltip title="Patient name from appointment" arrow placement="top">
                                    <input
                                        type="text"
                                        readOnly
                                        placeholder="Patient Name"
                                        className={`input-field ${formData.PatientId ? 'success' : ''}`}
                                        style={{ ...inputStyle, backgroundColor: '#f9fafb' }}
                                        value={formData.patientName || ''}
                                    />
                                </Tooltip>
                            </div>
                            <div>
                                <label style={labelStyle}>MRD No</label>
                                <Tooltip title="Medical Record Number" arrow placement="top">
                                    <input
                                        type="text"
                                        readOnly
                                        className="input-field"
                                        style={{ ...inputStyle, backgroundColor: '#f9fafb' }}
                                        value={formData.patientMRD || ''}
                                    />
                                </Tooltip>
                            </div>
                            <div>
                                <label style={labelStyle}>Phone No</label>
                                <input
                                    type="text"
                                    readOnly
                                    className="input-field"
                                    style={{ ...inputStyle, backgroundColor: '#f9fafb' }}
                                    value={formData.patientPhone || ''}
                                />
                            </div>
                            <div>
                                <label style={labelStyle}>
                                    Doctor <span style={{ color: '#dc2626' }}>*</span>
                                </label>
                                <Tooltip title="Doctor from appointment" arrow placement="top">
                                    <input
                                        type="text"
                                        readOnly
                                        className={`input-field ${formData.DoctorId ? 'success' : ''}`}
                                        style={{ ...inputStyle, backgroundColor: '#f9fafb' }}
                                        value={formData.DoctorName || ''}
                                    />
                                </Tooltip>
                            </div>
                            <div>
                                <label style={labelStyle}>Entry Date</label>
                                <Tooltip title="Date of examination" arrow placement="top">
                                    <input
                                        type="date"
                                        className="input-field"
                                        style={inputStyle}
                                        value={formData.DoctorEntryDate}
                                        onChange={(e) => handleFormChange('DoctorEntryDate', e.target.value)}
                                    />
                                </Tooltip>
                            </div>
                        </div>

                        <div style={{ marginTop: '10px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                            <div>
                                <label style={labelStyle}>
                                    Chief Complaint <span style={{ color: '#dc2626' }}>*</span>
                                </label>
                                <Tooltip title="Describe the patient's primary reason for visit (min 10 characters)" arrow placement="top">
                                    <textarea
                                        className={`input-field ${validationErrors.ChiefComplaint ? 'error' : formData.ChiefComplaint && formData.ChiefComplaint.length >= 10 ? 'success' : ''}`}
                                        style={{ ...inputStyle, minHeight: '45px', resize: 'vertical', height: 'auto', padding: '4px 8px' }}
                                        value={formData.ChiefComplaint}
                                        onChange={(e) => handleFormChange('ChiefComplaint', e.target.value)}
                                        placeholder="Enter patient's main complaint..."
                                    />
                                </Tooltip>
                                {validationErrors.ChiefComplaint && (
                                    <div style={{ fontSize: '9px', color: '#ef4444', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                                        <span>⚠️</span> {validationErrors.ChiefComplaint}
                                    </div>
                                )}
                                {formData.ChiefComplaint && formData.ChiefComplaint.length >= 10 && !validationErrors.ChiefComplaint && (
                                    <div style={{ fontSize: '9px', color: '#10b981', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                                        <span>✓</span> Looks good!
                                    </div>
                                )}
                            </div>
                            <div>
                                <label style={labelStyle}>Diagnosis</label>
                                <Tooltip title="Enter the medical Diagnosis (min 5 characters)" arrow placement="top">
                                    <textarea
                                        className={`input-field ${validationErrors.Diagnosis ? 'error' : formData.Diagnosis && formData.Diagnosis.length >= 5 ? 'success' : ''}`}
                                        style={{ ...inputStyle, minHeight: '45px', resize: 'vertical', height: 'auto', padding: '4px 8px' }}
                                        value={formData.Diagnosis}
                                        onChange={(e) => handleFormChange('Diagnosis', e.target.value)}
                                        placeholder="Enter Diagnosis..."
                                    />
                                </Tooltip>
                                {validationErrors.Diagnosis && (
                                    <div style={{ fontSize: '9px', color: '#ef4444', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                                        <span>⚠️</span> {validationErrors.Diagnosis}
                                    </div>
                                )}
                                {formData.Diagnosis && formData.Diagnosis.length >= 5 && !validationErrors.Diagnosis && (
                                    <div style={{ fontSize: '9px', color: '#10b981', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                                        <span>✓</span> Looks good!
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Tabs Section */}
                    <Box sx={{ background: '#f3f4f6', padding: '12px 16px' }}>
                        <Tabs
                            value={activeTab}
                            onChange={(e, newValue) => setActiveTab(newValue)}
                            variant="fullWidth"
                            TabIndicatorProps={{ style: { display: 'none' } }}
                            sx={{
                                minHeight: 'auto',
                                '& .MuiTabs-flexContainer': {
                                    gap: '8px'
                                },
                                '& .MuiTab-root': {
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    textTransform: 'none',
                                    minHeight: '40px',
                                    padding: '8px 12px',
                                    color: '#6b7280',
                                    background: '#e5e7eb',
                                    borderRadius: '12px',
                                    transition: 'all 0.3s ease',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                    '&:hover': {
                                        background: '#f9fafb',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                                    }
                                },
                                '& .MuiTab-root.Mui-selected': {
                                    color: '#ffffff',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    fontWeight: '700',
                                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                                    transform: 'translateY(-2px)'
                                }
                            }}
                        >
                            <Tab
                                label={
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
                                        <span style={{ fontSize: '20px', lineHeight: '1' }}>🩺</span>
                                        <span style={{ fontSize: '12px', fontWeight: '600', letterSpacing: '0.3px' }}>Observations</span>
                                        {sectionCompletion.observations && (
                                            <span style={{
                                                fontSize: '10px',
                                                color: '#10b981',
                                                background: '#d1fae5',
                                                borderRadius: '50%',
                                                width: '16px',
                                                height: '16px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: '700'
                                            }}>✓</span>
                                        )}
                                    </div>
                                }
                                iconPosition="start"
                            />
                            <Tab
                                label={
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
                                        <span style={{ fontSize: '20px', lineHeight: '1' }}>💊</span>
                                        <span style={{ fontSize: '12px', fontWeight: '600', letterSpacing: '0.3px' }}>Prescriptions</span>
                                        {sectionCompletion.prescriptions && (
                                            <span style={{
                                                fontSize: '10px',
                                                color: '#10b981',
                                                background: '#d1fae5',
                                                borderRadius: '50%',
                                                width: '16px',
                                                height: '16px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: '700'
                                            }}>✓</span>
                                        )}
                                    </div>
                                }
                                iconPosition="start"
                            />
                            <Tab
                                label={
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
                                        <span style={{ fontSize: '20px', lineHeight: '1' }}>🏥</span>
                                        <span style={{ fontSize: '12px', fontWeight: '600', letterSpacing: '0.3px' }}>Treatments</span>
                                        {sectionCompletion.treatments && (
                                            <span style={{
                                                fontSize: '10px',
                                                color: '#10b981',
                                                background: '#d1fae5',
                                                borderRadius: '50%',
                                                width: '16px',
                                                height: '16px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: '700'
                                            }}>✓</span>
                                        )}
                                    </div>
                                }
                                iconPosition="start"
                            />
                            <Tab
                                label={
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
                                        <span style={{ fontSize: '20px', lineHeight: '1' }}>📝</span>
                                        <span style={{ fontSize: '12px', fontWeight: '600', letterSpacing: '0.3px' }}>Questionnaire</span>
                                        {sectionCompletion.Questionnaire && (
                                            <span style={{
                                                fontSize: '10px',
                                                color: '#10b981',
                                                background: '#d1fae5',
                                                borderRadius: '50%',
                                                width: '16px',
                                                height: '16px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: '700'
                                            }}>✓</span>
                                        )}
                                    </div>
                                }
                                iconPosition="start"
                            />
                            <Tab
                                label={
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
                                        <span style={{ fontSize: '20px', lineHeight: '1' }}>📋</span>
                                        <span style={{ fontSize: '12px', fontWeight: '600', letterSpacing: '0.3px' }}>History</span>
                                        {patientHistory.length > 0 && (
                                            <span style={{
                                                fontSize: '10px',
                                                color: '#667eea',
                                                background: '#e0e7ff',
                                                borderRadius: '10px',
                                                padding: '2px 6px',
                                                fontWeight: '700'
                                            }}>{patientHistory.length}</span>
                                        )}
                                    </div>
                                }
                                iconPosition="start"
                            />
                            <Tab
                                label={
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
                                        <span style={{ fontSize: '20px', lineHeight: '1' }}>📅</span>
                                        <span style={{ fontSize: '12px', fontWeight: '600', letterSpacing: '0.3px' }}>Session Appt</span>
                                        {sessionAppointments.length > 0 && (
                                            <span style={{
                                                fontSize: '10px',
                                                color: '#f59e0b',
                                                background: '#fef3c7',
                                                borderRadius: '10px',
                                                padding: '2px 6px',
                                                fontWeight: '700'
                                            }}>{sessionAppointments.length}</span>
                                        )}
                                    </div>
                                }
                                iconPosition="start"
                            />
                        </Tabs>
                    </Box>

                    {/* Tab Content */}
                    <div className="tab-content" style={{ padding: '12px 16px' }}>
                        {/* Observations Tab */}
                        {activeTab === 0 && (
                            <div>
                                <div style={{
                                    background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
                                    padding: '10px',
                                    borderRadius: '6px',
                                    marginBottom: '12px'
                                }}>
                                    <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#667eea', margin: '0 0 8px 0' }}>
                                        Add Observation
                                    </h3>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr) auto', gap: '8px', alignItems: 'end' }}>
                                        <div>
                                            <label style={labelStyle}>Observation Type *</label>
                                            <select
                                                className="input-field"
                                                style={inputStyle}
                                                value={currentObservation.ObservationType}
                                                onChange={(e) => setCurrentObservation({ ...currentObservation, ObservationType: e.target.value })}
                                            >
                                                <option value="">Select Type</option>
                                                <option value="Blood Pressure">Blood Pressure</option>
                                                <option value="Temperature">Temperature</option>
                                                <option value="Heart Rate">Heart Rate</option>
                                                <option value="Weight">Weight</option>
                                                <option value="Height">Height</option>
                                                <option value="Blood Sugar">Blood Sugar</option>
                                                <option value="Oxygen Level">Oxygen Level</option>
                                                <option value="Other">Other</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Value *</label>
                                            <input
                                                type="text"
                                                className="input-field"
                                                style={inputStyle}
                                                value={currentObservation.ObservationValue}
                                                onChange={(e) => setCurrentObservation({ ...currentObservation, ObservationValue: e.target.value })}
                                                placeholder="Enter value"
                                            />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Unit</label>
                                            <input
                                                type="text"
                                                className="input-field"
                                                style={inputStyle}
                                                value={currentObservation.Unit}
                                                onChange={(e) => setCurrentObservation({ ...currentObservation, Unit: e.target.value })}
                                                placeholder="e.g., mmHg, °F"
                                            />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Remarks</label>
                                            <input
                                                type="text"
                                                className="input-field"
                                                style={inputStyle}
                                                value={currentObservation.Remarks}
                                                onChange={(e) => setCurrentObservation({ ...currentObservation, Remarks: e.target.value })}
                                                placeholder="Additional notes"
                                            />
                                        </div>
                                        <Tooltip title="Add this observation to the list" arrow placement="right">
                                            <button
                                                onClick={addObservation}
                                                style={{
                                                    ...buttonStyle,
                                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                    color: 'white',
                                                    height: '32px',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                <span style={{ fontSize: '14px' }}>➕</span>
                                                Add Observation
                                            </button>
                                        </Tooltip>
                                    </div>
                                </div>

                                {/* Observations Table */}
                                {observations.length > 0 && (
                                    <TableContainer component={Paper} className="advanced-table">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow style={{
                                                    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                                                    borderBottom: '3px solid #8b9aed'
                                                }}>
                                                    <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Type</TableCell>
                                                    <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Value</TableCell>
                                                    <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Unit</TableCell>
                                                    <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Remarks</TableCell>
                                                    <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', textAlign: 'center', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Action</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {observations.map((obs, index) => (
                                                    <TableRow
                                                        key={obs.id}
                                                        className="table-row-animated"
                                                        style={{
                                                            background: index % 2 === 0 ? '#ffffff' : '#f8fafc',
                                                            borderLeft: '4px solid transparent',
                                                            animationDelay: `${index * 0.05}s`
                                                        }}
                                                    >
                                                        <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#475569', fontWeight: '600' }}>{obs.ObservationType}</TableCell>
                                                        <TableCell style={{ fontSize: '13px', fontWeight: '700', padding: '6px 10px', color: '#0f172a' }}>{obs.ObservationValue}</TableCell>
                                                        <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#64748b', fontStyle: 'italic' }}>{obs.Unit}</TableCell>
                                                        <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#64748b' }}>{obs.Remarks}</TableCell>
                                                        <TableCell style={{ textAlign: 'center', padding: '6px 10px' }}>
                                                            <button
                                                                onClick={() => deleteObservation(obs.id)}
                                                                style={{
                                                                    background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                                                                    border: '1px solid #fca5a5',
                                                                    borderRadius: '6px',
                                                                    cursor: 'pointer',
                                                                    fontSize: '12px',
                                                                    padding: '3px 8px',
                                                                    transition: 'all 0.3s ease',
                                                                    boxShadow: '0 2px 4px rgba(239, 68, 68, 0.1)'
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    e.target.style.transform = 'scale(1.1) rotate(5deg)';
                                                                    e.target.style.background = 'linear-gradient(135deg, #fca5a5 0%, #f87171 100%)';
                                                                    e.target.style.boxShadow = '0 4px 8px rgba(239, 68, 68, 0.3)';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.target.style.transform = 'scale(1) rotate(0deg)';
                                                                    e.target.style.background = 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)';
                                                                    e.target.style.boxShadow = '0 2px 4px rgba(239, 68, 68, 0.1)';
                                                                }}
                                                                title="Delete"
                                                            >
                                                                🗑️
                                                            </button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </div>
                        )}

                        {/* Prescriptions Tab */}
                        {activeTab === 1 && (
                            <div>
                                <div style={{
                                    background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
                                    padding: '10px',
                                    borderRadius: '6px',
                                    marginBottom: '12px'
                                }}>
                                    <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#667eea', margin: '0 0 8px 0' }}>
                                        Add Prescription
                                    </h3>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr 1fr 0.7fr 1.5fr auto', gap: '8px', alignItems: 'end' }}>
                                        <div>
                                            <label style={labelStyle}>Medicine Name *</label>
                                            <select
                                                className="input-field"
                                                style={inputStyle}
                                                value={currentPrescription.ItemId}
                                                onChange={(e) => {
                                                    const selectedMedicine = medicines.find(m => m.Id === parseInt(e.target.value));
                                                    setCurrentPrescription({
                                                        ...currentPrescription,
                                                        ItemId: e.target.value,
                                                        MedicineName: selectedMedicine ? selectedMedicine.Name : ''
                                                    });
                                                }}
                                            >
                                                <option value="">Select Medicine</option>
                                                {medicines.map(medicine => (
                                                    <option key={medicine.Id} value={medicine.Id}>{medicine.Name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Dosage *</label>
                                            <input
                                                type="text"
                                                className="input-field"
                                                style={inputStyle}
                                                value={currentPrescription.Dosage}
                                                onChange={(e) => setCurrentPrescription({ ...currentPrescription, Dosage: e.target.value })}
                                                placeholder="e.g., 500mg"
                                            />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Frequency</label>
                                            <select
                                                className="input-field"
                                                style={inputStyle}
                                                value={currentPrescription.Frequency}
                                                onChange={(e) => setCurrentPrescription({ ...currentPrescription, Frequency: e.target.value })}
                                            >
                                                <option value="">Select Frequency</option>
                                                <option value="Once Daily">Once Daily</option>
                                                <option value="Twice Daily">Twice Daily</option>
                                                <option value="Three Times Daily">Three Times Daily</option>
                                                <option value="Four Times Daily">Four Times Daily</option>
                                                <option value="Every 4 Hours">Every 4 Hours</option>
                                                <option value="Every 6 Hours">Every 6 Hours</option>
                                                <option value="As Needed">As Needed</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Duration</label>
                                            <input
                                                type="text"
                                                className="input-field"
                                                style={inputStyle}
                                                value={currentPrescription.Duration}
                                                onChange={(e) => setCurrentPrescription({ ...currentPrescription, Duration: e.target.value })}
                                                placeholder="e.g., 7 days"
                                            />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Instructions</label>
                                            <input
                                                type="text"
                                                className="input-field"
                                                style={inputStyle}
                                                value={currentPrescription.Instructions}
                                                onChange={(e) => setCurrentPrescription({ ...currentPrescription, Instructions: e.target.value })}
                                                placeholder="e.g., Take after meals"
                                            />
                                        </div>
                                        <Tooltip title="Add this prescription to the list" arrow placement="right">
                                            <button
                                                onClick={addPrescription}
                                                style={{
                                                    ...buttonStyle,
                                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                    color: 'white',
                                                    height: '32px',
                                                    whiteSpace: 'nowrap',
                                                    width: 'auto',
                                                    minWidth: '140px'
                                                }}
                                            >
                                                <span style={{ fontSize: '14px' }}>➕</span>
                                                Add Prescription
                                            </button>
                                        </Tooltip>
                                    </div>
                                </div>

                                {/* Prescriptions Table */}
                                {prescriptions.length > 0 && (
                                    <TableContainer component={Paper} className="advanced-table">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow style={{ background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', borderBottom: '3px solid #8b9aed' }}><TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Medicine</TableCell>
                                                    <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Dosage</TableCell>
                                                    <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Frequency</TableCell>
                                                    <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Duration</TableCell>
                                                    <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Instructions</TableCell>
                                                    <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', textAlign: 'center', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Action</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {prescriptions.map((pres, index) => (
                                                    <TableRow
                                                        key={pres.id}
                                                        className="table-row-animated"
                                                        style={{
                                                            background: index % 2 === 0 ? '#ffffff' : '#f8fafc',
                                                            borderLeft: '4px solid transparent',
                                                            animationDelay: `${index * 0.05}s`
                                                        }}
                                                    >
                                                        <TableCell style={{ fontSize: '13px', fontWeight: '700', padding: '6px 10px', color: '#0f172a' }}>{pres.MedicineName}</TableCell>
                                                        <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#475569', fontWeight: '600' }}>{pres.Dosage}</TableCell>
                                                        <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#64748b' }}>{pres.Frequency}</TableCell>
                                                        <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#64748b' }}>{pres.Duration}</TableCell>
                                                        <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#64748b', fontStyle: 'italic' }}>{pres.Instructions}</TableCell>
                                                        <TableCell style={{ textAlign: 'center', padding: '6px 8px' }}>
                                                            <button
                                                                onClick={() => deletePrescription(pres.id)}
                                                                style={{
                                                                    background: 'transparent',
                                                                    border: 'none',
                                                                    cursor: 'pointer',
                                                                    fontSize: '12px',
                                                                    padding: '0px 2px',
                                                                    transition: 'transform 0.2s ease'
                                                                }}
                                                                onMouseEnter={(e) => e.target.style.transform = 'scale(1.2)'}
                                                                onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                                                                title="Delete"
                                                            >
                                                                🗑️
                                                            </button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </div>
                        )}

                        {/* Treatments Tab */}
                        {activeTab === 2 && (
                            <div>
                                <div style={{
                                    background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
                                    padding: '10px',
                                    borderRadius: '6px',
                                    marginBottom: '12px'
                                }}>
                                    <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#667eea', margin: '0 0 8px 0' }}>
                                        Add Treatment
                                    </h3>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 0.8fr 0.8fr 0.6fr 1fr auto', gap: '8px', alignItems: 'end' }}>
                                        <div>
                                            <label style={labelStyle}>Treatment Name *</label>
                                            <select
                                                className="input-field"
                                                style={inputStyle}
                                                value={currentTreatment.TreatmentId}
                                                onChange={(e) => {
                                                    const selectedTreatment = treatmentsList.find(t => t.Id === parseInt(e.target.value));
                                                    setCurrentTreatment({
                                                        ...currentTreatment,
                                                        TreatmentId: e.target.value,
                                                        treatmentName: selectedTreatment ? selectedTreatment.Name : '',
                                                        SessionId: '',
                                                        sessionName: ''
                                                    });
                                                    if (e.target.value) {
                                                        fetchTreatmentSessions(e.target.value);
                                                    } else {
                                                        setTreatmentSessions([]);
                                                    }
                                                }}
                                            >
                                                <option value="">Select Treatment</option>
                                                {treatmentsList.map(treatment => (
                                                    <option key={treatment.Id} value={treatment.Id}>{treatment.Name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Session</label>
                                            <select
                                                className="input-field"
                                                style={inputStyle}
                                                value={currentTreatment.SessionId}
                                                onChange={(e) => {
                                                    const selectedSession = treatmentSessions.find(s => s.Id === parseInt(e.target.value));
                                                    setCurrentTreatment({
                                                        ...currentTreatment,
                                                        SessionId: e.target.value,
                                                        sessionName: selectedSession ? selectedSession.Name : '',
                                                        Rate: selectedSession && selectedSession.Rate ? selectedSession.Rate : ''
                                                    });
                                                }}
                                                disabled={!currentTreatment.TreatmentId}
                                            >
                                                <option value="">Select Session</option>
                                                {treatmentSessions.map(session => (
                                                    <option key={session.Id} value={session.Id}>{session.Name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Start Date</label>
                                            <input
                                                type="date"
                                                className="input-field"
                                                style={inputStyle}
                                                value={currentTreatment.StartDate}
                                                onChange={(e) => setCurrentTreatment({ ...currentTreatment, StartDate: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>End Date</label>
                                            <input
                                                type="date"
                                                className="input-field"
                                                style={inputStyle}
                                                value={currentTreatment.EndDate}
                                                onChange={(e) => setCurrentTreatment({ ...currentTreatment, EndDate: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Rate</label>
                                            <input
                                                type="number"
                                                className="input-field"
                                                style={{ ...inputStyle, textAlign: 'right' }}
                                                value={currentTreatment.Rate}
                                                onChange={(e) => setCurrentTreatment({ ...currentTreatment, Rate: e.target.value })}
                                                placeholder="0.00"
                                            />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Description</label>
                                            <input
                                                type="text"
                                                className="input-field"
                                                style={inputStyle}
                                                value={currentTreatment.Description}
                                                onChange={(e) => setCurrentTreatment({ ...currentTreatment, Description: e.target.value })}
                                                placeholder="Treatment Description"
                                            />
                                        </div>
                                        <Tooltip title="Add this treatment to the list" arrow placement="right">
                                            <button
                                                onClick={addTreatment}
                                                style={{
                                                    ...buttonStyle,
                                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                    color: 'white',
                                                    height: '32px',
                                                    whiteSpace: 'nowrap',
                                                    width: 'auto',
                                                    minWidth: '140px'
                                                }}
                                            >
                                                <span style={{ fontSize: '14px' }}>➕</span>
                                                Add Treatment
                                            </button>
                                        </Tooltip>
                                    </div>
                                </div>

                                {/* Treatments Table */}
                                {treatments.length > 0 && (
                                    <TableContainer component={Paper} className="advanced-table">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow style={{
                                                    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                                                    borderBottom: '3px solid #8b9aed'
                                                }}>
                                                    <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Treatment</TableCell>
                                                    <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Session</TableCell>
                                                    <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Description</TableCell>
                                                    <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Start Date</TableCell>
                                                    <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>End Date</TableCell>
                                                    <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', textAlign: 'right', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Rate</TableCell>
                                                    <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', textAlign: 'center', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Action</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {treatments.map((treat, index) => (
                                                    <TableRow
                                                        key={treat.id}
                                                        className="table-row-animated"
                                                        style={{
                                                            background: index % 2 === 0 ? '#ffffff' : '#f8fafc',
                                                            borderLeft: '4px solid transparent',
                                                            animationDelay: `${index * 0.05}s`
                                                        }}
                                                    >
                                                        <TableCell style={{ fontSize: '13px', fontWeight: '700', padding: '6px 10px', color: '#0f172a' }}>{treat.treatmentName}</TableCell>
                                                        <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#8b5cf6', fontWeight: '600' }}>{treat.sessionName || '-'}</TableCell>
                                                        <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#64748b' }}>{treat.Description}</TableCell>
                                                        <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#475569', fontWeight: '600' }}>{treat.StartDate}</TableCell>
                                                        <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#475569', fontWeight: '600' }}>{treat.EndDate}</TableCell>
                                                        <TableCell style={{ fontSize: '13px', textAlign: 'right', padding: '6px 10px', color: '#059669', fontWeight: '700' }}>{treat.Rate}</TableCell>
                                                        <TableCell style={{ textAlign: 'center', padding: '6px 10px' }}>
                                                            <button
                                                                onClick={() => deleteTreatment(treat.id)}
                                                                style={{
                                                                    background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                                                                    border: '1px solid #fca5a5',
                                                                    borderRadius: '6px',
                                                                    cursor: 'pointer',
                                                                    fontSize: '12px',
                                                                    padding: '3px 8px',
                                                                    transition: 'all 0.3s ease',
                                                                    boxShadow: '0 2px 4px rgba(239, 68, 68, 0.1)'
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    e.target.style.transform = 'scale(1.1) rotate(5deg)';
                                                                    e.target.style.background = 'linear-gradient(135deg, #fca5a5 0%, #f87171 100%)';
                                                                    e.target.style.boxShadow = '0 4px 8px rgba(239, 68, 68, 0.3)';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.target.style.transform = 'scale(1) rotate(0deg)';
                                                                    e.target.style.background = 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)';
                                                                    e.target.style.boxShadow = '0 2px 4px rgba(239, 68, 68, 0.1)';
                                                                }}
                                                                title="Delete"
                                                            >
                                                                🗑️
                                                            </button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </div>
                        )}

                        {/* Questionnaire Tab */}
                        {activeTab === 3 && (
                            <div>
                                <div style={{
                                    background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
                                    padding: '10px',
                                    borderRadius: '6px',
                                    marginBottom: '12px'
                                }}>
                                    <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#667eea', margin: '0 0 8px 0' }}>
                                        Add Question & Answer
                                    </h3>
                                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 150px 1.5fr auto', gap: '8px', alignItems: 'end', marginBottom: '8px' }}>
                                        <div>
                                            <label style={labelStyle}>Select Question from List *</label>
                                            <select
                                                className="input-field"
                                                style={inputStyle}
                                                value={currentQuestion.QuestioniertId}
                                                onChange={(e) => {
                                                    const selectedQuestion = QuestionsList.find(q => q.Id === parseInt(e.target.value));
                                                    setCurrentQuestion({
                                                        ...currentQuestion,
                                                        QuestioniertId: e.target.value,
                                                        Question: selectedQuestion ? selectedQuestion.Question : ''
                                                    });
                                                }}
                                            >
                                                <option value="">Select Question</option>
                                                {QuestionsList.map(q => (
                                                    <option key={q.Id} value={q.Id}>{q.Question}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Answer Type *</label>
                                            <select
                                                className="input-field"
                                                style={inputStyle}
                                                value={currentQuestion.AnswerType}
                                                onChange={(e) => setCurrentQuestion({ ...currentQuestion, AnswerType: e.target.value, Answer: '' })}
                                            >
                                                <option value="text">Text Input</option>
                                                <option value="select">Select Option</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Answer *</label>
                                            {currentQuestion.AnswerType === 'select' ? (
                                                <select
                                                    className="input-field"
                                                    style={inputStyle}
                                                    value={currentQuestion.Answer}
                                                    onChange={(e) => setCurrentQuestion({ ...currentQuestion, Answer: e.target.value })}
                                                >
                                                    <option value="">Select Answer</option>
                                                    <option value="Yes">Yes</option>
                                                    <option value="No">No</option>
                                                    <option value="Sometimes">Sometimes</option>
                                                    <option value="Frequently">Frequently</option>
                                                    <option value="Rarely">Rarely</option>
                                                    <option value="Never">Never</option>
                                                    <option value="Mild">Mild</option>
                                                    <option value="Moderate">Moderate</option>
                                                    <option value="Severe">Severe</option>
                                                </select>
                                            ) : (
                                                <input
                                                    type="text"
                                                    className="input-field"
                                                    style={inputStyle}
                                                    value={currentQuestion.Answer}
                                                    onChange={(e) => setCurrentQuestion({ ...currentQuestion, Answer: e.target.value })}
                                                    placeholder="Enter Answer"
                                                />
                                            )}
                                        </div>
                                        <Tooltip title="Add this Question to the Questionnaire" arrow placement="right">
                                            <button
                                                onClick={addQuestion}
                                                style={{
                                                    ...buttonStyle,
                                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                    color: 'white',
                                                    height: '32px',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                <span style={{ fontSize: '14px' }}>➕</span>
                                                Add Question
                                            </button>
                                        </Tooltip>
                                    </div>
                                    <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <span>💡</span>
                                        <span>Select a Question from the dropdown, choose Answer type (text or select), then provide the Answer</span>
                                    </div>
                                </div>

                                {/* Questionnaire Table */}
                                {Questionnaire.length > 0 && (
                                    <TableContainer component={Paper} className="advanced-table">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow style={{
                                                    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                                                    borderBottom: '3px solid #8b9aed'
                                                }}>
                                                    <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Question ID</TableCell>
                                                    <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Question</TableCell>
                                                    <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Answer</TableCell>
                                                    <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', textAlign: 'center', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Action</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {Questionnaire.map((q, index) => (
                                                    <TableRow
                                                        key={q.id}
                                                        className="table-row-animated"
                                                        style={{
                                                            background: index % 2 === 0 ? '#ffffff' : '#f8fafc',
                                                            borderLeft: '4px solid transparent',
                                                            animationDelay: `${index * 0.05}s`
                                                        }}
                                                    >
                                                        <TableCell style={{ fontSize: '12px', fontWeight: '700', padding: '6px 10px', color: '#667eea' }}>
                                                            {q.QuestioniertId ? `#${q.QuestioniertId}` : 'N/A'}
                                                        </TableCell>
                                                        <TableCell style={{ fontSize: '13px', fontWeight: '700', padding: '6px 10px', color: '#0f172a' }}>{q.Question}</TableCell>
                                                        <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#475569', fontWeight: '600' }}>{q.Answer}</TableCell>
                                                        <TableCell style={{ textAlign: 'center', padding: '6px 10px' }}>
                                                            <button
                                                                onClick={() => deleteQuestion(q.id)}
                                                                style={{
                                                                    background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                                                                    border: '1px solid #fca5a5',
                                                                    borderRadius: '6px',
                                                                    cursor: 'pointer',
                                                                    fontSize: '12px',
                                                                    padding: '3px 8px',
                                                                    transition: 'all 0.3s ease',
                                                                    boxShadow: '0 2px 4px rgba(239, 68, 68, 0.1)'
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    e.target.style.transform = 'scale(1.1) rotate(5deg)';
                                                                    e.target.style.background = 'linear-gradient(135deg, #fca5a5 0%, #f87171 100%)';
                                                                    e.target.style.boxShadow = '0 4px 8px rgba(239, 68, 68, 0.3)';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.target.style.transform = 'scale(1) rotate(0deg)';
                                                                    e.target.style.background = 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)';
                                                                    e.target.style.boxShadow = '0 2px 4px rgba(239, 68, 68, 0.1)';
                                                                }}
                                                                title="Delete"
                                                            >
                                                                🗑️
                                                            </button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </div>
                        )}

                        {/* History Tab */}
                        {activeTab === 4 && (
                            <div>
                                <div style={{
                                    background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
                                    padding: '10px',
                                    borderRadius: '6px',
                                    marginBottom: '12px'
                                }}>
                                    <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#667eea', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span>📋</span> Patient Visit History
                                        {patientHistory.length > 0 && (
                                            <span style={{
                                                fontSize: '11px',
                                                color: '#667eea',
                                                background: '#e0e7ff',
                                                borderRadius: '10px',
                                                padding: '2px 8px',
                                                fontWeight: '600'
                                            }}>{patientHistory.length} visits</span>
                                        )}
                                    </h3>
                                    <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>
                                        Click on a visit row to expand and view detailed information
                                    </p>
                                </div>

                                {/* Loading State */}
                                {isLoadingHistory && (
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '40px',
                                        background: '#f8fafc',
                                        borderRadius: '12px',
                                        border: '1px solid #e2e8f0'
                                    }}>
                                        <div style={{
                                            width: '40px',
                                            height: '40px',
                                            border: '3px solid #e5e7eb',
                                            borderTop: '3px solid #667eea',
                                            borderRadius: '50%',
                                            animation: 'spin 1s linear infinite'
                                        }}></div>
                                        <p style={{ marginTop: '12px', color: '#6b7280', fontSize: '13px' }}>Loading patient history...</p>
                                    </div>
                                )}

                                {/* Empty State */}
                                {!isLoadingHistory && patientHistory.length === 0 && (
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '40px',
                                        background: '#f8fafc',
                                        borderRadius: '12px',
                                        border: '1px dashed #cbd5e1'
                                    }}>
                                        <span style={{ fontSize: '48px', marginBottom: '12px' }}>📭</span>
                                        <p style={{ color: '#64748b', fontSize: '14px', fontWeight: '600', margin: '0 0 4px 0' }}>No previous history found</p>
                                        <p style={{ color: '#94a3b8', fontSize: '12px', margin: 0 }}>This patient has no previous visit records</p>
                                    </div>
                                )}

                                {/* Patient History List */}
                                {!isLoadingHistory && patientHistory.length > 0 && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {patientHistory.map((visit, index) => (
                                            <div key={visit.DoctorEntry?.Id || index} style={{
                                                background: '#ffffff',
                                                borderRadius: '10px',
                                                border: '1px solid #e2e8f0',
                                                overflow: 'hidden',
                                                boxShadow: expandedHistoryIndex === index ? '0 4px 12px rgba(102, 126, 234, 0.15)' : '0 1px 3px rgba(0,0,0,0.05)',
                                                transition: 'all 0.3s ease'
                                            }}>
                                                {/* Visit Summary Row */}
                                                <div
                                                    onClick={() => toggleHistoryExpand(index)}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        padding: '12px 16px',
                                                        cursor: 'pointer',
                                                        background: expandedHistoryIndex === index
                                                            ? 'linear-gradient(135deg, #667eea10 0%, #764ba210 100%)'
                                                            : '#ffffff',
                                                        borderBottom: expandedHistoryIndex === index ? '1px solid #e2e8f0' : 'none',
                                                        transition: 'all 0.2s ease'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (expandedHistoryIndex !== index) {
                                                            e.currentTarget.style.background = '#f8fafc';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (expandedHistoryIndex !== index) {
                                                            e.currentTarget.style.background = '#ffffff';
                                                        }
                                                    }}
                                                >
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        marginRight: '16px'
                                                    }}>
                                                        <span style={{
                                                            fontSize: '14px',
                                                            transition: 'transform 0.3s ease',
                                                            transform: expandedHistoryIndex === index ? 'rotate(90deg)' : 'rotate(0deg)'
                                                        }}>▶</span>
                                                        <span style={{
                                                            fontSize: '11px',
                                                            color: '#667eea',
                                                            fontWeight: '700',
                                                            background: '#e0e7ff',
                                                            padding: '2px 8px',
                                                            borderRadius: '6px'
                                                        }}>Visit #{patientHistory.length - index}</span>
                                                    </div>
                                                    <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr 1.5fr 1.5fr', gap: '12px', alignItems: 'center' }}>
                                                        <div>
                                                            <div style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Date</div>
                                                            <div style={{ fontSize: '12px', fontWeight: '600', color: '#1e293b' }}>
                                                                {visit.DoctorEntry?.DoctorEntryDate ? new Date(visit.DoctorEntry.DoctorEntryDate).toLocaleDateString() : 'N/A'}
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <div style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Doctor</div>
                                                            <div style={{ fontSize: '12px', fontWeight: '600', color: '#1e293b' }}>{visit.DoctorEntry?.DoctorName || 'N/A'}</div>
                                                        </div>
                                                        <div>
                                                            <div style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Chief Complaint</div>
                                                            <div style={{ fontSize: '12px', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {visit.DoctorEntry?.ChiefComplaint || 'N/A'}
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <div style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Diagnosis</div>
                                                            <div style={{ fontSize: '12px', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {visit.DoctorEntry?.Diagnosis || 'N/A'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div style={{
                                                        fontSize: '11px',
                                                        color: '#667eea',
                                                        fontWeight: '600',
                                                        marginLeft: '12px'
                                                    }}>
                                                        {expandedHistoryIndex === index ? 'Collapse' : 'Expand'}
                                                    </div>
                                                </div>

                                                {/* Expanded Details */}
                                                {expandedHistoryIndex === index && (
                                                    <div style={{ padding: '16px', background: '#fafbfc' }}>
                                                        {/* Observations Section */}
                                                        {visit.ObservationDetails && visit.ObservationDetails.length > 0 && (
                                                            <div style={{
                                                                marginBottom: '12px',
                                                                background: '#fef3c7',
                                                                border: '1px solid #fcd34d',
                                                                borderRadius: '8px',
                                                                padding: '12px'
                                                            }}>
                                                                <h4 style={{ fontSize: '12px', fontWeight: '700', color: '#92400e', margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                    <span>🩺</span> Observations ({visit.ObservationDetails.length})
                                                                </h4>
                                                                <TableContainer component={Paper} style={{ boxShadow: 'none' }}>
                                                                    <Table size="small">
                                                                        <TableHead>
                                                                            <TableRow style={{ background: '#fef3c7' }}>
                                                                                <TableCell style={{ fontWeight: '700', fontSize: '10px', color: '#92400e', padding: '6px 10px', textTransform: 'uppercase' }}>Type</TableCell>
                                                                                <TableCell style={{ fontWeight: '700', fontSize: '10px', color: '#92400e', padding: '6px 10px', textTransform: 'uppercase' }}>Value</TableCell>
                                                                                <TableCell style={{ fontWeight: '700', fontSize: '10px', color: '#92400e', padding: '6px 10px', textTransform: 'uppercase' }}>Unit</TableCell>
                                                                                <TableCell style={{ fontWeight: '700', fontSize: '10px', color: '#92400e', padding: '6px 10px', textTransform: 'uppercase' }}>Remarks</TableCell>
                                                                            </TableRow>
                                                                        </TableHead>
                                                                        <TableBody>
                                                                            {visit.ObservationDetails.map((obs, obsIndex) => (
                                                                                <TableRow key={obsIndex} style={{ background: obsIndex % 2 === 0 ? '#fffbeb' : '#fef3c7' }}>
                                                                                    <TableCell style={{ fontSize: '11px', padding: '6px 10px', color: '#78350f' }}>{obs.ObservationType}</TableCell>
                                                                                    <TableCell style={{ fontSize: '11px', padding: '6px 10px', fontWeight: '600', color: '#78350f' }}>{obs.ObservationValue}</TableCell>
                                                                                    <TableCell style={{ fontSize: '11px', padding: '6px 10px', color: '#92400e' }}>{obs.Unit || '-'}</TableCell>
                                                                                    <TableCell style={{ fontSize: '11px', padding: '6px 10px', color: '#92400e' }}>{obs.Remarks || '-'}</TableCell>
                                                                                </TableRow>
                                                                            ))}
                                                                        </TableBody>
                                                                    </Table>
                                                                </TableContainer>
                                                            </div>
                                                        )}

                                                        {/* Prescriptions Section */}
                                                        {visit.PrescriptionDetails && visit.PrescriptionDetails.length > 0 && (
                                                            <div style={{
                                                                marginBottom: '12px',
                                                                background: '#dbeafe',
                                                                border: '1px solid #93c5fd',
                                                                borderRadius: '8px',
                                                                padding: '12px'
                                                            }}>
                                                                <h4 style={{ fontSize: '12px', fontWeight: '700', color: '#1e40af', margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                    <span>💊</span> Prescriptions ({visit.PrescriptionDetails.length})
                                                                </h4>
                                                                <TableContainer component={Paper} style={{ boxShadow: 'none' }}>
                                                                    <Table size="small">
                                                                        <TableHead>
                                                                            <TableRow style={{ background: '#dbeafe' }}>
                                                                                <TableCell style={{ fontWeight: '700', fontSize: '10px', color: '#1e40af', padding: '6px 10px', textTransform: 'uppercase' }}>Medicine</TableCell>
                                                                                <TableCell style={{ fontWeight: '700', fontSize: '10px', color: '#1e40af', padding: '6px 10px', textTransform: 'uppercase' }}>Dosage</TableCell>
                                                                                <TableCell style={{ fontWeight: '700', fontSize: '10px', color: '#1e40af', padding: '6px 10px', textTransform: 'uppercase' }}>Frequency</TableCell>
                                                                                <TableCell style={{ fontWeight: '700', fontSize: '10px', color: '#1e40af', padding: '6px 10px', textTransform: 'uppercase' }}>Duration</TableCell>
                                                                                <TableCell style={{ fontWeight: '700', fontSize: '10px', color: '#1e40af', padding: '6px 10px', textTransform: 'uppercase' }}>Instructions</TableCell>
                                                                            </TableRow>
                                                                        </TableHead>
                                                                        <TableBody>
                                                                            {visit.PrescriptionDetails.map((pres, presIndex) => (
                                                                                <TableRow key={presIndex} style={{ background: presIndex % 2 === 0 ? '#eff6ff' : '#dbeafe' }}>
                                                                                    <TableCell style={{ fontSize: '11px', padding: '6px 10px', fontWeight: '600', color: '#1e3a8a' }}>{pres.MedicineName}</TableCell>
                                                                                    <TableCell style={{ fontSize: '11px', padding: '6px 10px', color: '#1e40af' }}>{pres.Dosage || '-'}</TableCell>
                                                                                    <TableCell style={{ fontSize: '11px', padding: '6px 10px', color: '#1e40af' }}>{pres.Frequency || '-'}</TableCell>
                                                                                    <TableCell style={{ fontSize: '11px', padding: '6px 10px', color: '#1e40af' }}>{pres.Duration || '-'}</TableCell>
                                                                                    <TableCell style={{ fontSize: '11px', padding: '6px 10px', color: '#1e40af', fontStyle: 'italic' }}>{pres.Instructions || '-'}</TableCell>
                                                                                </TableRow>
                                                                            ))}
                                                                        </TableBody>
                                                                    </Table>
                                                                </TableContainer>
                                                            </div>
                                                        )}

                                                        {/* Documents Section */}
                                                        {visit.DocumentDetails && visit.DocumentDetails.length > 0 && (
                                                            <div style={{
                                                                marginBottom: '12px',
                                                                background: '#dcfce7',
                                                                border: '1px solid #86efac',
                                                                borderRadius: '8px',
                                                                padding: '12px'
                                                            }}>
                                                                <h4 style={{ fontSize: '12px', fontWeight: '700', color: '#166534', margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                    <span>📄</span> Documents ({visit.DocumentDetails.length})
                                                                </h4>
                                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                                    {visit.DocumentDetails.map((doc, docIndex) => (
                                                                        <div key={docIndex} style={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: '8px',
                                                                            background: '#f0fdf4',
                                                                            border: '1px solid #bbf7d0',
                                                                            borderRadius: '6px',
                                                                            padding: '8px 12px'
                                                                        }}>
                                                                            <span style={{ fontSize: '16px' }}>📎</span>
                                                                            <div>
                                                                                <div style={{ fontSize: '11px', fontWeight: '600', color: '#166534' }}>{doc.DocumentName}</div>
                                                                                <div style={{ fontSize: '10px', color: '#15803d' }}>{doc.DocumentType}</div>
                                                                            </div>
                                                                            {doc.DocumentPath && (
                                                                                <a
                                                                                    href={doc.DocumentPath}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    style={{
                                                                                        fontSize: '10px',
                                                                                        color: '#ffffff',
                                                                                        background: '#22c55e',
                                                                                        padding: '4px 8px',
                                                                                        borderRadius: '4px',
                                                                                        textDecoration: 'none',
                                                                                        fontWeight: '600'
                                                                                    }}
                                                                                >
                                                                                    View
                                                                                </a>
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Treatments Section */}
                                                        {visit.TreatmentDetails && visit.TreatmentDetails.length > 0 && (
                                                            <div style={{
                                                                marginBottom: '12px',
                                                                background: '#f3e8ff',
                                                                border: '1px solid #d8b4fe',
                                                                borderRadius: '8px',
                                                                padding: '12px'
                                                            }}>
                                                                <h4 style={{ fontSize: '12px', fontWeight: '700', color: '#7c3aed', margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                    <span>🏥</span> Treatments ({visit.TreatmentDetails.length})
                                                                </h4>
                                                                <TableContainer component={Paper} style={{ boxShadow: 'none' }}>
                                                                    <Table size="small">
                                                                        <TableHead>
                                                                            <TableRow style={{ background: '#f3e8ff' }}>
                                                                                <TableCell style={{ fontWeight: '700', fontSize: '10px', color: '#7c3aed', padding: '6px 10px', textTransform: 'uppercase' }}>Treatment Name</TableCell>
                                                                                <TableCell style={{ fontWeight: '700', fontSize: '10px', color: '#7c3aed', padding: '6px 10px', textTransform: 'uppercase' }}>Session</TableCell>
                                                                                <TableCell style={{ fontWeight: '700', fontSize: '10px', color: '#7c3aed', padding: '6px 10px', textTransform: 'uppercase' }}>Description</TableCell>
                                                                                <TableCell style={{ fontWeight: '700', fontSize: '10px', color: '#7c3aed', padding: '6px 10px', textTransform: 'uppercase' }}>Start Date</TableCell>
                                                                                <TableCell style={{ fontWeight: '700', fontSize: '10px', color: '#7c3aed', padding: '6px 10px', textTransform: 'uppercase' }}>End Date</TableCell>
                                                                                <TableCell style={{ fontWeight: '700', fontSize: '10px', color: '#7c3aed', padding: '6px 10px', textTransform: 'uppercase' }}>Rate</TableCell>
                                                                                <TableCell style={{ fontWeight: '700', fontSize: '10px', color: '#7c3aed', padding: '6px 10px', textTransform: 'uppercase' }}>Sessions</TableCell>
                                                                            </TableRow>
                                                                        </TableHead>
                                                                        <TableBody>
                                                                            {visit.TreatmentDetails.map((treat, treatIndex) => (
                                                                                <TableRow key={treatIndex} style={{ background: treatIndex % 2 === 0 ? '#faf5ff' : '#f3e8ff' }}>
                                                                                    <TableCell style={{ fontSize: '11px', padding: '6px 10px', fontWeight: '600', color: '#6b21a8' }}>{treat.TreatmentName}</TableCell>
                                                                                    <TableCell style={{ fontSize: '11px', padding: '6px 10px', color: '#7c3aed' }}>{treat.SessionName || '-'}</TableCell>
                                                                                    <TableCell style={{ fontSize: '11px', padding: '6px 10px', color: '#7c3aed' }}>{treat.Description || '-'}</TableCell>
                                                                                    <TableCell style={{ fontSize: '11px', padding: '6px 10px', color: '#7c3aed' }}>{treat.StartDate ? new Date(treat.StartDate).toLocaleDateString() : '-'}</TableCell>
                                                                                    <TableCell style={{ fontSize: '11px', padding: '6px 10px', color: '#7c3aed' }}>{treat.EndDate ? new Date(treat.EndDate).toLocaleDateString() : '-'}</TableCell>
                                                                                    <TableCell style={{ fontSize: '11px', padding: '6px 10px', color: '#7c3aed', fontWeight: '600' }}>{treat.Rate || '-'}</TableCell>
                                                                                    <TableCell style={{ fontSize: '11px', padding: '6px 10px', color: '#7c3aed' }}>{treat.SittingOrSession || '-'}</TableCell>
                                                                                </TableRow>
                                                                            ))}
                                                                        </TableBody>
                                                                    </Table>
                                                                </TableContainer>
                                                            </div>
                                                        )}

                                                        {/* Questionnaire Section */}
                                                        {visit.TreatmentQuestionier && visit.TreatmentQuestionier.length > 0 && (
                                                            <div style={{
                                                                background: '#ccfbf1',
                                                                border: '1px solid #5eead4',
                                                                borderRadius: '8px',
                                                                padding: '12px'
                                                            }}>
                                                                <h4 style={{ fontSize: '12px', fontWeight: '700', color: '#0f766e', margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                    <span>❓</span> Questionnaire ({visit.TreatmentQuestionier.length})
                                                                </h4>
                                                                <TableContainer component={Paper} style={{ boxShadow: 'none' }}>
                                                                    <Table size="small">
                                                                        <TableHead>
                                                                            <TableRow style={{ background: '#ccfbf1' }}>
                                                                                <TableCell style={{ fontWeight: '700', fontSize: '10px', color: '#0f766e', padding: '6px 10px', textTransform: 'uppercase' }}>Question</TableCell>
                                                                                <TableCell style={{ fontWeight: '700', fontSize: '10px', color: '#0f766e', padding: '6px 10px', textTransform: 'uppercase' }}>Answer</TableCell>
                                                                            </TableRow>
                                                                        </TableHead>
                                                                        <TableBody>
                                                                            {visit.TreatmentQuestionier.map((q, qIndex) => (
                                                                                <TableRow key={qIndex} style={{ background: qIndex % 2 === 0 ? '#f0fdfa' : '#ccfbf1' }}>
                                                                                    <TableCell style={{ fontSize: '11px', padding: '6px 10px', color: '#115e59' }}>{q.Question}</TableCell>
                                                                                    <TableCell style={{ fontSize: '11px', padding: '6px 10px', fontWeight: '600', color: '#0f766e' }}>{q.Answer}</TableCell>
                                                                                </TableRow>
                                                                            ))}
                                                                        </TableBody>
                                                                    </Table>
                                                                </TableContainer>
                                                            </div>
                                                        )}

                                                        {/* No Details Message */}
                                                        {(!visit.ObservationDetails || visit.ObservationDetails.length === 0) &&
                                                            (!visit.PrescriptionDetails || visit.PrescriptionDetails.length === 0) &&
                                                            (!visit.DocumentDetails || visit.DocumentDetails.length === 0) &&
                                                            (!visit.TreatmentDetails || visit.TreatmentDetails.length === 0) &&
                                                            (!visit.TreatmentQuestionier || visit.TreatmentQuestionier.length === 0) && (
                                                                <div style={{
                                                                    textAlign: 'center',
                                                                    padding: '20px',
                                                                    color: '#94a3b8',
                                                                    fontSize: '12px'
                                                                }}>
                                                                    <span style={{ fontSize: '24px', display: 'block', marginBottom: '8px' }}>📝</span>
                                                                    No detailed records available for this visit
                                                                </div>
                                                            )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Session Appointments Tab */}
                        {activeTab === 5 && (
                            <div>
                                {/* Patient Info Summary */}
                                <div style={{
                                    background: 'linear-gradient(135deg, #fef3c715 0%, #f59e0b15 100%)',
                                    padding: '12px',
                                    borderRadius: '8px',
                                    marginBottom: '12px',
                                    border: '1px solid #fcd34d'
                                }}>
                                    <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#b45309', margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span>👤</span> Patient Information
                                    </h3>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                                        <div>
                                            <div style={{ fontSize: '10px', color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Patient Name</div>
                                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#78350f' }}>{formData.patientName || 'N/A'}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '10px', color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>MRD No</div>
                                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#78350f' }}>{formData.patientMRD || 'N/A'}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '10px', color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Phone</div>
                                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#78350f' }}>{formData.patientPhone || 'N/A'}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '10px', color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Doctor</div>
                                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#78350f' }}>{formData.DoctorName || 'N/A'}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Add Session Appointment Form */}
                                <div style={{
                                    background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
                                    padding: '12px',
                                    borderRadius: '8px',
                                    marginBottom: '12px'
                                }}>
                                    <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#667eea', margin: '0 0 10px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span>📅</span> Schedule Treatment Session Appointment
                                    </h3>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1.5fr 0.8fr 1fr 0.8fr 1.5fr auto', gap: '10px', alignItems: 'end' }}>
                                        <div>
                                            <label style={labelStyle}>Treatment *</label>
                                            <select
                                                className="input-field"
                                                style={inputStyle}
                                                value={currentSessionAppointment.TreatmentId}
                                                onChange={(e) => {
                                                    const selectedTreatment = treatmentsList.find(t => t.Id === parseInt(e.target.value));
                                                    setCurrentSessionAppointment({
                                                        ...currentSessionAppointment,
                                                        TreatmentId: e.target.value,
                                                        TreatmentName: selectedTreatment ? selectedTreatment.Name : '',
                                                        SessionId: '',
                                                        SessionName: '',
                                                        Rate: ''
                                                    });
                                                    if (e.target.value) {
                                                        fetchSessionTreatmentSessions(e.target.value);
                                                    } else {
                                                        setSessionTreatmentSessions([]);
                                                    }
                                                }}
                                            >
                                                <option value="">Select Treatment</option>
                                                {treatmentsList.map(treatment => (
                                                    <option key={treatment.Id} value={treatment.Id}>{treatment.Name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Session *</label>
                                            <select
                                                className="input-field"
                                                style={inputStyle}
                                                value={currentSessionAppointment.SessionId}
                                                onChange={(e) => {
                                                    const selectedSession = sessionTreatmentSessions.find(s => s.Id === parseInt(e.target.value));
                                                    // Check for Rate, Amount, or Price field from API
                                                    const sessionRate = selectedSession ? (selectedSession.Rate || selectedSession.Amount || selectedSession.Price || 0) : '';
                                                    setCurrentSessionAppointment({
                                                        ...currentSessionAppointment,
                                                        SessionId: e.target.value,
                                                        SessionName: selectedSession ? selectedSession.Name : '',
                                                        Rate: sessionRate
                                                    });
                                                }}
                                                disabled={!currentSessionAppointment.TreatmentId}
                                            >
                                                <option value="">Select Session</option>
                                                {sessionTreatmentSessions.map(session => (
                                                    <option key={session.Id} value={session.Id}>{session.Name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Rate</label>
                                            <input
                                                type="number"
                                                className="input-field"
                                                style={{ ...inputStyle, textAlign: 'right', backgroundColor: '#f9fafb' }}
                                                value={currentSessionAppointment.Rate}
                                                readOnly
                                                placeholder="0.00"
                                            />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Date *</label>
                                            <input
                                                type="date"
                                                className="input-field"
                                                style={inputStyle}
                                                value={currentSessionAppointment.AppointmentDate}
                                                onChange={(e) => setCurrentSessionAppointment({ ...currentSessionAppointment, AppointmentDate: e.target.value })}
                                                min={getCurrentDate()}
                                            />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Time *</label>
                                            <input
                                                type="time"
                                                className="input-field"
                                                style={inputStyle}
                                                value={currentSessionAppointment.AppointmentTime}
                                                onChange={(e) => setCurrentSessionAppointment({ ...currentSessionAppointment, AppointmentTime: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Notes</label>
                                            <input
                                                type="text"
                                                className="input-field"
                                                style={inputStyle}
                                                value={currentSessionAppointment.Notes}
                                                onChange={(e) => setCurrentSessionAppointment({ ...currentSessionAppointment, Notes: e.target.value })}
                                                placeholder="Optional notes..."
                                            />
                                        </div>
                                        <Tooltip title="Add session appointment" arrow placement="right">
                                            <button
                                                onClick={addSessionAppointment}
                                                style={{
                                                    padding: '6px 12px',
                                                    borderRadius: '6px',
                                                    border: 'none',
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px',
                                                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                                    color: 'white',
                                                    height: '32px',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                <span style={{ fontSize: '14px' }}>➕</span>
                                                Add Appointment
                                            </button>
                                        </Tooltip>
                                    </div>
                                </div>

                                {/* Session Appointments Table */}
                                {sessionAppointments.length > 0 && (
                                    <TableContainer component={Paper} className="advanced-table">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow style={{
                                                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                                    borderBottom: '3px solid #fcd34d'
                                                }}>
                                                    <TableCell style={{ color: '#ffffff', fontWeight: '700', fontSize: '11px', padding: '8px 12px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Treatment</TableCell>
                                                    <TableCell style={{ color: '#ffffff', fontWeight: '700', fontSize: '11px', padding: '8px 12px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Session</TableCell>
                                                    <TableCell style={{ color: '#ffffff', fontWeight: '700', fontSize: '11px', padding: '8px 12px', letterSpacing: '0.5px', textTransform: 'uppercase', textAlign: 'right' }}>Rate</TableCell>
                                                    <TableCell style={{ color: '#ffffff', fontWeight: '700', fontSize: '11px', padding: '8px 12px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Date</TableCell>
                                                    <TableCell style={{ color: '#ffffff', fontWeight: '700', fontSize: '11px', padding: '8px 12px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Time</TableCell>
                                                    <TableCell style={{ color: '#ffffff', fontWeight: '700', fontSize: '11px', padding: '8px 12px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Notes</TableCell>
                                                    <TableCell style={{ color: '#ffffff', fontWeight: '700', fontSize: '11px', textAlign: 'center', padding: '8px 12px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Action</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {sessionAppointments.map((apt, index) => (
                                                    <TableRow
                                                        key={apt.id}
                                                        className="table-row-animated"
                                                        style={{
                                                            background: index % 2 === 0 ? '#fffbeb' : '#fef3c7',
                                                            borderLeft: '4px solid transparent',
                                                            animationDelay: `${index * 0.05}s`
                                                        }}
                                                    >
                                                        <TableCell style={{ fontSize: '13px', fontWeight: '700', padding: '8px 12px', color: '#78350f' }}>{apt.TreatmentName}</TableCell>
                                                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#92400e', fontWeight: '600' }}>{apt.SessionName}</TableCell>
                                                        <TableCell style={{ fontSize: '13px', textAlign: 'right', padding: '8px 12px', color: '#059669', fontWeight: '700' }}>{apt.Rate || '-'}</TableCell>
                                                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#78350f', fontWeight: '600' }}>
                                                            {apt.AppointmentDate ? new Date(apt.AppointmentDate).toLocaleDateString() : '-'}
                                                        </TableCell>
                                                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#78350f', fontWeight: '600' }}>{apt.AppointmentTime}</TableCell>
                                                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#92400e', fontStyle: 'italic' }}>{apt.Notes || '-'}</TableCell>
                                                        <TableCell style={{ textAlign: 'center', padding: '8px 12px' }}>
                                                            <button
                                                                onClick={() => deleteSessionAppointment(apt.id)}
                                                                style={{
                                                                    background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                                                                    border: '1px solid #fca5a5',
                                                                    borderRadius: '6px',
                                                                    cursor: 'pointer',
                                                                    fontSize: '12px',
                                                                    padding: '4px 10px',
                                                                    transition: 'all 0.3s ease',
                                                                    boxShadow: '0 2px 4px rgba(239, 68, 68, 0.1)'
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    e.target.style.transform = 'scale(1.1)';
                                                                    e.target.style.background = 'linear-gradient(135deg, #fca5a5 0%, #f87171 100%)';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.target.style.transform = 'scale(1)';
                                                                    e.target.style.background = 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)';
                                                                }}
                                                                title="Delete"
                                                            >
                                                                🗑️
                                                            </button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}

                                {/* Empty State */}
                                {sessionAppointments.length === 0 && (
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '40px',
                                        background: '#fffbeb',
                                        borderRadius: '12px',
                                        border: '1px dashed #fcd34d',
                                        marginBottom: '12px'
                                    }}>
                                        <span style={{ fontSize: '48px', marginBottom: '12px' }}>📅</span>
                                        <p style={{ color: '#92400e', fontSize: '14px', fontWeight: '600', margin: '0 0 4px 0' }}>No session appointments scheduled</p>
                                        <p style={{ color: '#b45309', fontSize: '12px', margin: 0 }}>Select a treatment and session above to schedule an appointment</p>
                                    </div>
                                )}

                                {/* Consultation & Total Amount Section */}
                                <div style={{
                                    background: 'linear-gradient(135deg, #10b98115 0%, #05966915 100%)',
                                    padding: '16px',
                                    borderRadius: '10px',
                                    border: '1px solid #86efac',
                                    marginTop: '12px'
                                }}>
                                    <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#166534', margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span>💰</span> Billing Summary
                                    </h3>

                                    {/* Consultation Checkbox */}
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        marginBottom: '16px',
                                        padding: '12px',
                                        background: '#ffffff',
                                        borderRadius: '8px',
                                        border: '1px solid #d1fae5'
                                    }}>
                                        <input
                                            type="checkbox"
                                            id="consultationNeeded"
                                            checked={isConsultationNeeded}
                                            onChange={(e) => setIsConsultationNeeded(e.target.checked)}
                                            style={{
                                                width: '18px',
                                                height: '18px',
                                                cursor: 'pointer',
                                                accentColor: '#10b981'
                                            }}
                                        />
                                        <label
                                            htmlFor="consultationNeeded"
                                            style={{
                                                fontSize: '13px',
                                                fontWeight: '600',
                                                color: '#166534',
                                                cursor: 'pointer',
                                                flex: 1
                                            }}
                                        >
                                            Consultation Needed
                                        </label>
                                        <span style={{
                                            fontSize: '14px',
                                            fontWeight: '700',
                                            color: isConsultationNeeded ? '#059669' : '#9ca3af',
                                            background: isConsultationNeeded ? '#d1fae5' : '#f3f4f6',
                                            padding: '4px 12px',
                                            borderRadius: '6px',
                                            transition: 'all 0.3s ease'
                                        }}>
                                            ₹ {(parseFloat(consultationAmount) || 0).toFixed(2)}
                                        </span>
                                    </div>

                                    {/* Amount Breakdown */}
                                    <div style={{
                                        background: '#ffffff',
                                        borderRadius: '8px',
                                        padding: '12px',
                                        border: '1px solid #d1fae5'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                            <span style={{ fontSize: '12px', color: '#6b7280' }}>Session Appointments ({sessionAppointments.length})</span>
                                            <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                                                ₹ {sessionAppointments.reduce((sum, apt) => sum + (parseFloat(apt.Rate) || 0), 0).toFixed(2)}
                                            </span>
                                        </div>
                                        {isConsultationNeeded && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                <span style={{ fontSize: '12px', color: '#6b7280' }}>Consultation Fee</span>
                                                <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                                                    ₹ {(parseFloat(consultationAmount) || 0).toFixed(2)}
                                                </span>
                                            </div>
                                        )}
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            paddingTop: '10px',
                                            borderTop: '2px solid #10b981',
                                            marginTop: '8px'
                                        }}>
                                            <span style={{ fontSize: '14px', fontWeight: '700', color: '#166534' }}>Total Amount</span>
                                            <span style={{
                                                fontSize: '18px',
                                                fontWeight: '800',
                                                color: '#059669',
                                                background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                                                padding: '6px 16px',
                                                borderRadius: '8px'
                                            }}>
                                                ₹ {calculateTotalAmount().toFixed(2)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                </div>



            </div>
        </Fade>
    );
};

export default DoctorEntryCreateIds;
